import React from 'react';
import {
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  AdjustmentsHorizontalIcon,
} from '@heroicons/react/24/outline';
import { RackLayoutData } from './tipos';

interface InspectorRackSeleccionadoProps {
  rack: RackLayoutData;
  pasoGrid: number;
  onMover: (dx: number, dz: number) => void;
  onRotar: (deltaGrados: number) => void;
  onCambiarDimensiones: (updates: Partial<RackLayoutData>) => void;
  onDuplicar: () => void;
  onEliminar: () => void;
  onDeseleccionar: () => void;
}

export const InspectorRackSeleccionado: React.FC<InspectorRackSeleccionadoProps> = ({
  rack,
  pasoGrid,
  onMover,
  onRotar,
  onCambiarDimensiones,
  onDuplicar,
  onEliminar,
  onDeseleccionar,
}) => {
  return (
    <div className="absolute bottom-6 right-6 z-30 w-80 bg-slate-900/95 backdrop-blur-xl border border-blue-500/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col text-white animate-fadeIn">
      {/* Header */}
      <div className="p-3.5 border-b border-slate-800 bg-slate-950/80 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse"></div>
          <div>
            <h4 className="font-extrabold text-sm text-cyan-300 leading-none">{rack.rack}</h4>
            <span className="text-[10px] text-slate-400 font-semibold">
              {rack.zona} · {rack.pasillo}
            </span>
          </div>
        </div>
        <button
          onClick={onDeseleccionar}
          className="p-1 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-all"
          title="Deseleccionar (Esc)"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-4 text-xs">
        {/* 1. Mover Rack (Pad Direccional) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Posición 3D (X: {rack.x.toFixed(1)}m, Z: {rack.z.toFixed(1)}m)
            </span>
            <span className="text-[10px] bg-slate-800 px-1.5 py-0.5 rounded text-blue-400 font-mono">
              Paso: {pasoGrid}m
            </span>
          </div>

          <div className="flex flex-col items-center gap-1.5 bg-slate-950/60 p-2.5 rounded-xl border border-slate-800/80">
            <button
              onClick={() => onMover(0, -pasoGrid)}
              className="p-1.5 bg-slate-800 hover:bg-blue-600 rounded-lg text-slate-300 hover:text-white transition-all shadow-sm"
              title="Mover hacia el Fondo (Z-)"
            >
              <ArrowUpIcon className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={() => onMover(-pasoGrid, 0)}
                className="p-1.5 bg-slate-800 hover:bg-blue-600 rounded-lg text-slate-300 hover:text-white transition-all shadow-sm"
                title="Mover a la Izquierda (X-)"
              >
                <ArrowLeftIcon className="w-4 h-4" />
              </button>
              <div className="w-7 h-7 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center text-[10px] font-bold text-blue-400">
                ✛
              </div>
              <button
                onClick={() => onMover(pasoGrid, 0)}
                className="p-1.5 bg-slate-800 hover:bg-blue-600 rounded-lg text-slate-300 hover:text-white transition-all shadow-sm"
                title="Mover a la Derecha (X+)"
              >
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={() => onMover(0, pasoGrid)}
              className="p-1.5 bg-slate-800 hover:bg-blue-600 rounded-lg text-slate-300 hover:text-white transition-all shadow-sm"
              title="Mover hacia el Frente (Z+)"
            >
              <ArrowDownIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 2. Rotar Rack */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
              Orientación / Giro
            </span>
            <span className="text-[10px] font-extrabold text-amber-400">
              {rack.rotacion}°
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onRotar(-90)}
              className="flex items-center justify-center gap-1.5 py-1.5 px-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl text-slate-200 font-bold transition-all text-[11px]"
            >
              <ArrowPathIcon className="w-3.5 h-3.5 scale-x-[-1]" />
              Girar -90°
            </button>
            <button
              onClick={() => onRotar(90)}
              className="flex items-center justify-center gap-1.5 py-1.5 px-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl text-slate-200 font-bold transition-all text-[11px]"
            >
              <ArrowPathIcon className="w-3.5 h-3.5" />
              Girar +90°
            </button>
          </div>
        </div>

        {/* 3. Cambiar Dimensiones */}
        <div className="space-y-2 pt-2 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
              <AdjustmentsHorizontalIcon className="w-3.5 h-3.5" />
              Dimensiones
            </span>
          </div>

          {/* Selector de Niveles */}
          <div className="flex items-center justify-between bg-slate-950/60 p-2 rounded-xl border border-slate-800/80">
            <span className="text-slate-300 font-medium">Niveles ({rack.niveles}):</span>
            <div className="flex items-center gap-1">
              <button
                disabled={rack.niveles <= 1}
                onClick={() => onCambiarDimensiones({ niveles: Math.max(1, rack.niveles - 1) })}
                className="w-6 h-6 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-xs font-bold"
              >
                -
              </button>
              <span className="w-6 text-center font-extrabold text-blue-400">{rack.niveles}</span>
              <button
                disabled={rack.niveles >= 6}
                onClick={() => onCambiarDimensiones({ niveles: Math.min(6, rack.niveles + 1) })}
                className="w-6 h-6 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-xs font-bold"
              >
                +
              </button>
            </div>
          </div>

          {/* Módulos de Pallet (Ancho) */}
          <div className="grid grid-cols-3 gap-1.5">
            <button
              onClick={() => onCambiarDimensiones({ ancho: 2.8 })}
              className={`py-1.5 px-1 rounded-xl text-[10px] font-bold text-center transition-all ${
                rack.ancho <= 3.0
                  ? 'bg-blue-600 text-white shadow'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              2.8m
            </button>
            <button
              onClick={() => onCambiarDimensiones({ ancho: 3.8 })}
              className={`py-1.5 px-1 rounded-xl text-[10px] font-bold text-center transition-all ${
                rack.ancho > 3.0 && rack.ancho <= 4.2
                  ? 'bg-blue-600 text-white shadow'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              3.8m
            </button>
            <button
              onClick={() => onCambiarDimensiones({ ancho: 4.8 })}
              className={`py-1.5 px-1 rounded-xl text-[10px] font-bold text-center transition-all ${
                rack.ancho > 4.2
                  ? 'bg-blue-600 text-white shadow'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              4.8m
            </button>
          </div>

          {/* Capacidad en Toneladas */}
          <div className="flex items-center justify-between bg-slate-950/60 p-2 rounded-xl border border-slate-800/80">
            <span className="text-slate-300 font-medium">Tolerancia Carga:</span>
            <div className="flex items-center gap-1">
              <input
                type="number"
                step="0.5"
                min="0.5"
                max="20"
                value={rack.max_ton}
                onChange={(e) => onCambiarDimensiones({ max_ton: Number(e.target.value) || 3.5 })}
                className="w-16 bg-slate-800 border border-slate-700 rounded px-2 py-0.5 text-right font-bold text-emerald-400 focus:outline-none"
              />
              <span className="text-slate-400 font-bold">t</span>
            </div>
          </div>
        </div>

        {/* 4. Duplicar y Eliminar */}
        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-800">
          <button
            onClick={onDuplicar}
            className="flex items-center justify-center gap-1.5 py-2 px-2.5 bg-blue-600/30 hover:bg-blue-600/50 border border-blue-500/40 rounded-xl text-blue-300 font-bold transition-all shadow-sm"
          >
            <DocumentDuplicateIcon className="w-3.5 h-3.5" />
            Duplicar
          </button>
          <button
            onClick={onEliminar}
            className="flex items-center justify-center gap-1.5 py-2 px-2.5 bg-red-600/20 hover:bg-red-600/40 border border-red-500/40 rounded-xl text-red-400 font-bold transition-all shadow-sm"
          >
            <TrashIcon className="w-3.5 h-3.5" />
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
};
